/**
 * Utility/settings routes a user must always be able to reach, regardless of
 * onboarding-completion or organization state — account management, billing,
 * admin, support. Shared between useRequireOnboarding.ts (the soft-navigate
 * guard) and services/api/client.ts's NO_HIERARCHY interceptor (the hard
 * window.location.href redirect that, in practice, is the mechanism that
 * actually decides where a user lands — see bug-registry.md, 2026-07-29
 * safety-review finding).
 *
 * A single shared list matters because a user on one of these pages can
 * still trigger an org-scoped background call (e.g. PillarLayout's
 * ActivePopBanner, mounted unconditionally by Layout on every authenticated
 * page including /settings) that 404s NO_HIERARCHY — if the interceptor's
 * own exemption check drifted from this list, a user on an "exempt" page
 * could still get hard-redirected away from it, defeating the entire point
 * of the exemption.
 */
export const ONBOARDING_EXEMPT_PREFIXES = [
  // Spec 289: /onboard is now just a stub redirect (App.tsx's
  // OnboardRedirect) to /register or /helm — kept exempt so it stays the
  // SINGLE authority for that route; without this, PrivateRoute's own
  // useRequireOnboarding() call would also fire on /onboard and race
  // OnboardRedirect's own navigate() with a second, independently-computed
  // one (same destination today, but two decision-makers for one route is
  // exactly the split-brain pattern this spec exists to eliminate).
  '/onboard',
  // Spec 289: onboarding's real destination is now /register itself
  // (Rhythm/Personalize/Boundaries live in its step machine, including the
  // Didit-resume path via ?resume=hierarchy). MUST stay exempt: an
  // org-less, onboarding-incomplete user genuinely on /register (e.g.
  // mid-Didit-resume) can still trigger a background NO_HIERARCHY call
  // (client.ts's interceptor, PillarLayout's ActivePopBanner etc.) — without
  // this entry, that interceptor would hard window.location.href-redirect
  // /register to itself, reloading the page and dropping in-progress step
  // state instead of just leaving the user where they already are.
  '/register',
  '/organizations',
  '/admin',
  '/age-verification',
  '/billing',
  '/help',
  '/notifications',
  '/settings',
  // 0zzz-uhy: the safeword page is a safety exit. A user in no hierarchy gets an honest dead end there, and a
  // background NO_HIERARCHY 404 (Layout's pop banner) must not hard-redirect them away from it.
  '/safeword',
  // 0zzz-uhx: a removed owner (or a buddy) with no org follows the level-1 / level-2 push deep link here to confirm or
  // cancel a timer; a background NO_HIERARCHY 404 must not hard-redirect them away. Covers /safety/check-in/:id too.
  '/safety/check-in',
  '/support',
  '/privacy-policy',
  '/account',
];

export function isOnboardingExemptPath(pathname: string): boolean {
  return ONBOARDING_EXEMPT_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}
